<script setup lang="ts">

import { faFilterCircleXmark } from '@fortawesome/pro-regular-svg-icons'

import { FilterDateRange } from '@shared/ui/filters'
import { BtnIcon, BtnPrimary } from '@shared/ui/buttons'
import { AppInput } from '@shared/ui/inputs'
import { AppMultiSelect, AppSelect } from '@shared/ui/selects'
import { AppText } from '@shared/ui/typography'

import { useFiltersOrders } from '../model/filters-orders.hook'

defineOptions({
  name: 'FiltersOrders',
})

withDefaults(defineProps<{
  /**
   * Search, Type and Lessons bought belong to the global list. The customer's Orders tab shows Date and Status
   * only and keeps the legacy bar: Clear filters and Search right after the fields.
   */
  withExtraFilters?: boolean
}>(), {
  withExtraFilters: true,
})

const {
  refSearch,
  refDateRange,
  refStatuses,
  refType,
  refLessonsFrom,
  refLessonsTo,
  statusOptions,
  typeOptions,
  lessonsMin,
  lessonsMax,
  isLoading,
  clearFilters,
  submitSearch,
} = useFiltersOrders()
</script>

<template>
  <div :class="['filters-orders', { 'filters-orders--customer-tab': !withExtraFilters }]">
    <div class="filters-orders__fields">
      <AppInput
        v-if="withExtraFilters"
        v-model="refSearch"
        class="filters-orders__search"
        label="Search"
        placeholder="Search"
        :disabled="isLoading"
      />

      <FilterDateRange
        v-model="refDateRange"
        class="filters-orders__date"
        label="Date"
        :placeholder="withExtraFilters ? 'Select range' : 'Search'"
        :disabled="isLoading"
      />

      <AppMultiSelect
        v-model="refStatuses"
        class="filters-orders__status"
        :options="statusOptions"
        label="Status"
        placeholder="All"
        :disabled="isLoading"
        clearable
      />

      <AppSelect
        v-if="withExtraFilters"
        v-model="refType"
        class="filters-orders__select"
        :options="typeOptions"
        label="Type"
        placeholder="All"
        :disabled="isLoading"
        clearable
      />

      <div
        v-if="withExtraFilters"
        class="filters-orders__lessons"
      >
        <AppText
          name="body2"
          color="slate-900"
        >
          Lessons bought
        </AppText>
        <div class="filters-orders__lessons-range">
          <AppInput
            v-model="refLessonsFrom"
            class="filters-orders__lessons-input"
            type="number"
            placeholder="From"
            :min="lessonsMin"
            :max="lessonsMax"
            :disabled="isLoading"
          />
          <AppInput
            v-model="refLessonsTo"
            class="filters-orders__lessons-input"
            type="number"
            placeholder="To"
            :min="lessonsMin"
            :max="lessonsMax"
            :disabled="isLoading"
          />
        </div>
      </div>
    </div>

    <div class="filters-orders__controls">
      <BtnIcon
        :icon="faFilterCircleXmark"
        color="red"
        tooltip-text="Clear filters"
        :disabled="isLoading"
        @click="clearFilters"
      />
      <BtnPrimary
        v-if="!withExtraFilters"
        text="Search"
        :disabled="isLoading"
        @click="submitSearch"
      />
    </div>
  </div>
</template>

<style scoped lang="scss" src="./FiltersOrders.scss" />
